import AutoCompleteTags from '../../../plugins/autocomplete/tags';
import {
    ComponentElements,
    ComponentInjector,
    ComponentObject,
    ComponentSelectors,
    ComponentService
} from '@atypon/ui-utils';

class Texts extends ComponentObject { }

class ClassList extends ComponentObject {
    autocompleteList = 'autoComplete autoComplete--tags rlist';
    autocompleteItem = 'autoComplete__item my-2';
    hidden = 'hidden'
}

class Selectors extends ComponentSelectors {
    autoComplete = '.autoComplete';
    autoCompleteWrapper = '.autoComplete_wrapper';
}

class Elements extends ComponentElements<Selectors> { }

interface AdvancedSearchAutoComplete extends ComponentService<Selectors, Elements, ClassList, Texts> { }
interface AdvancedSearchAutoCompleteConfigs {
    maxWords: number;
    maxChars: number;
    url: string;
}

interface AdvancedSearchAutoCompleteOptions {
    method: string;
    valueField: string;
    autoSelect: boolean;
    allowFreeEntries: boolean;
    displayField: string;
    cls: string;
}

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class AdvancedSearchAutoComplete {
    autoCompletes: AutoCompleteTags[];
    protected placeHolders: Map<HTMLInputElement, string> = new Map();
    protected results: { value: { value: string, label: string } }[] = [];
    protected _configs: AdvancedSearchAutoCompleteConfigs;
    protected get configs(): AdvancedSearchAutoCompleteConfigs {
        return {
            maxWords: 200,
            maxChars: 222,
            url: '/action/doSuggest'
        }
    }

    protected get options(): AdvancedSearchAutoCompleteOptions {
        return {
            method: 'GET',
            valueField: 'value',
            autoSelect: false,
            allowFreeEntries: false,
            displayField: 'label',
            cls: 'pr-4',
        };
    }

    /**
     * local uri for testing perposes
     *
     * @return {string}
     */
    get localURL(): string {
        return '/specs/libraries/ui-core/widgets/advanced-search/ajax/data.json';
    }

    protected elementRenderer(item, data) {
        const a = document.createElement('a');
        a.href = data.value.url;
        a.innerHTML = `<span>${data.value.highlight}</span>`;
        a.className = 'autoComplete__link';
        item.setAttribute('aria-label', a.innerText);
        item.innerHTML = '';
        item.appendChild(a);
        a.addEventListener("click", (event) => {
            event.preventDefault();
        });
    };

    constructor(readonly filters: HTMLElement[], readonly stateChanged: Function) {
        this.filters = filters;
        this.stateChanged = stateChanged;
        this.autoCompletes = [];
        this._configs = this.configs;
    }

    initialize(): void {
        this.addEventListeners();
    }

    addEventListeners(): void {
        this.filters.forEach(this.initAutoComplete.bind(this));
        this.autocompleteAnnouncer();
    }

    autocompleteAnnouncer(): void {
        const announcerContainer = document.querySelector('.advanced-search__autocomplete__announcer');
        const autocompleteLists = document.querySelectorAll('[id^="autoComplete_list_"]');
        const mutationCallback = function(mutationsList) {
            mutationsList.forEach(mutation => {
                if(mutation.target.children.length){
                    announcerContainer.innerHTML = `${mutation.target.children.length} results are available, use up and down arrow keys to navigate.`
                } else {
                    announcerContainer.innerHTML = `No results are available.`
                }
            });
        };
        const observer = new MutationObserver(mutationCallback);
        autocompleteLists.forEach(element => {
            observer.observe(element, { childList: true });
        });
    }

    protected urlWithQuery(item) {
        const term = item.value.trim().replace(/\s\s+/g, ' ');
        const { maxWords, maxChars, url } = this.configs;
        if (term.split(' ').length >= maxWords || term.length >= maxChars) {
            return '';
        }
        const { autoCompleteTarget, autoCompleteWithin } = item.dataset;

        let query = `?target=${autoCompleteTarget}&query=${encodeURIComponent(term)}`;
        if (autoCompleteWithin) {
            query = `${query}&within=${autoCompleteWithin}`;
        }
        // return `${this.localURL}${query}`;
        return `${url}${query}`;
    }

    protected buildConfigs(item: HTMLElement, i: number) {
        return {
            data: {
                src: async () => {
                    const url = this.urlWithQuery(item);
                    const data = await fetch(url);
                    return await data.json();
                },
                cache: false
            },
            selector: () => item,
            resultsList: {
                render: true,
                idName: 'autoComplete-' + i,
                class: this.classList.autocompleteList,
                maxResults: 10
            },
            resultItem: {
                class: this.classList.autocompleteItem,
                element: this.elementRenderer.bind(this),
            },
            highlight: false,
            sort: false,
            threshold: 2,
            preselectedItems: this.itemPreselectedValues(item),
            searchEngine: null,
            onTagRemoved: null
        }
    }

    protected itemPreselectedValues(item) {
        const { values } = item.dataset;
        return values ? JSON.parse(values) : [];
    }

    protected createAutoCompleteInstance(configs, item): AutoCompleteTags {
        return new AutoCompleteTags(configs, item)
    }

    protected initAutoComplete(item: HTMLInputElement, i: number) {
        const configs = this.buildConfigs(item, i);
        configs.searchEngine = (query: any, record: any) => this.onSearchEngine(autoCompleteInstance, query, record);
        configs.onTagRemoved = () => this.onTagRemoved(autoCompleteInstance);
        const autoCompleteInstance = this.createAutoCompleteInstance(configs, item);
        this.placeHolders.set(item, item.placeholder);
        this.autoCompletes.push(autoCompleteInstance);
        this.domUtils.closest(this.selectors.autoCompleteWrapper, item)
            ?.addEventListener('click', this.onAutoCompleteFocus.bind(this, item));
        this.onTagValuesChange(autoCompleteInstance);
        item.addEventListener('blur', this.onBlur.bind(this, autoCompleteInstance, item));
        item.addEventListener("selection", this.onSelection.bind(this, autoCompleteInstance, item));
        item.addEventListener('results', this.onResults.bind(this));
    }

    protected onSearchEngine(autoCompleteInstance: AutoCompleteTags, query: any, record: any) {
        const found = autoCompleteInstance.tagValues.find(item => {
            const [input] = item;
            return record.value == (input as HTMLInputElement).value
        })
        if (found) {
            return null;
        }
        return record.highlight || record.label;
    }

    protected onTagRemoved(autoCompleteInstance: AutoCompleteTags) {
        this.onTagValuesChange(autoCompleteInstance);
        this.stateChanged();
    }

    protected onAutoCompleteFocus(field: HTMLInputElement) {
        field.focus();
        field.parentElement.scrollTop = field.parentElement.scrollHeight;
    }

    protected onTagValuesChange({ field, tagValues }: AutoCompleteTags) {
        const hasTags = tagValues.length;
        if (hasTags) {
            field.removeAttribute('placeholder');
            field.style.width = "auto";
        } else {
            field.setAttribute('placeholder', this.placeHolders.get(field));
            field.style.width = "100%";
        }
    }

    protected onBlur(autocomplete, input, e) {
        if (e.relatedTarget && e.relatedTarget.closest(this.selectors.autoComplete)) {
            input.value = null;
            this.results = [];
            return;
        }
        if (this.results.length && this.results.length === 1) {
            const { label, value } = this.results.pop().value;
            autocomplete.createTagValue(label, value);
        }
        input.value = '';
        this.results = [];
    }

    protected onSelection(autocomplete: AutoCompleteTags, input: HTMLInputElement, event: CustomEvent) {
        const { label, value } = event.detail.selection.value;
        autocomplete.createTagValue(label, value);
        input.value = '';
        this.domUtils.toggleClasses(autocomplete.tagsPosition, this.classList.hidden, !autocomplete.tagValues.length);
        this.onTagValuesChange(autocomplete);
        this.stateChanged();
    }

    protected onResults({ detail }: CustomEvent) {
        this.results = detail.results;
    }

}

export default AdvancedSearchAutoComplete;

export {
    Elements as AdvancedSearchAutoCompleteElements,
    Selectors as AdvancedSearchAutoCompleteSelectors,
    ClassList as AdvancedSearchAutoCompleteClassList,
    Texts as AdvancedSearchAutoCompleteTexts
}